import { Injectable, Optional } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { HttpHeaders, HttpContext, HttpContextToken } from '@angular/common/http';
import { ToastrService, IndividualConfig } from 'ngx-toastr';
import { Store } from '../interfaces/operation-controls/stores.interface';

export const SKIP_ERROR_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root',
})
export class GlobalFunction {
  constructor(
    public router: Router,
    @Optional() private toastr: ToastrService,
  ) {}

  headers(skipError: boolean = false): {
    headers: HttpHeaders;
    context?: HttpContext;
  } {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept-language': 'es',
      Authorization: `Bearer ${this.leerToken()}`,
    });

    const options: { headers: HttpHeaders; context?: HttpContext } = {
      headers,
    };

    if (skipError) {
      options.context = new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true);
    }

    return options;
  }

  publicHeaders(): { headers: HttpHeaders } {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept-language': 'es',
    });
    return { headers };
  }

  headersMultipart(): { headers: HttpHeaders } {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.leerToken()}`,
    });
    return { headers };
  }
  centerPolygon(coords: google.maps.LatLngLiteral[]): {
    lat: number;
    lng: number;
  } {
    let sumLat = 0;
    let sumLng = 0;
    let lat = 0;
    let lng = 0;
    if (coords && coords.length > 0) {
      for (let i = 0; i < coords.length; i++) {
        const point = coords[i];
        const x = point.lat;
        const y = point.lng;
        sumLat += x;
        sumLng += y;
      }

      lat = sumLat / coords.length;
      lng = sumLng / coords.length;
    }
    return { lat, lng };
  }

  leerToken(): string | null {
    try {
      return localStorage.getItem('token');
    } catch {
      return null;
    }
  }

  validateError(e: unknown): Observable<never> {
    const normalized = (e as { status?: number; error?: unknown; statusText?: string }) || {};
    const status = normalized.status ?? 0;
    const error = normalized.error;
    const statusText = normalized.statusText ?? '';
    const userError =
      typeof error === 'object' && error !== null && 'user' in error
        ? ((error as { user?: unknown }).user ?? error)
        : error;
    const errorObj =
      typeof error === 'object' && error !== null ? (error as { detail?: unknown }) : {};

    switch (status) {
      case 0:
        this.showError('Servidor no disponible', 'Error de conexión');
        break;

      case 401:
        this.handleUnauthorizedError(error);
        break;

      case 403:
        this.showError('No tienes permisos para esta acción', 'Error 403');
        break;

      case 404:
        this.showError(String(errorObj.detail ?? 'Recurso no encontrado'), 'Error 404');
        break;

      case 500:
        this.showError(String(errorObj.detail ?? 'Error interno del servidor'), 'Error 500');
        break;

      default:
        this.handleValidationErrors(userError, status, error, statusText);
    }

    return throwError(() => e);
  }

  private handleUnauthorizedError(error: unknown): void {
    const errorObj =
      typeof error === 'object' && error !== null
        ? (error as { error?: unknown; detail?: unknown })
        : {};
    const message = String(errorObj.error ?? errorObj.detail ?? 'No autorizado');
    this.showError(message, 'Error 401');

    localStorage.removeItem('token');
    this.router.navigateByUrl('/login');
  }

  private handleValidationErrors(
    userError: unknown,
    status: number,
    error: unknown,
    statusText: string,
  ): void {
    if (!userError || typeof userError !== 'object') {
      this.showFallbackError(error, status, statusText);
      return;
    }

    const errors = this.extractFieldErrors(userError as Record<string, unknown>, statusText);

    if (errors.length === 0) {
      this.showFallbackError(error, status, statusText);
      return;
    }

    errors.forEach((errorMsg) => {
      this.showError(errorMsg, status ? `Error ${status}` : 'Error');
    });
  }

  private extractFieldErrors(userError: Record<string, unknown>, statusText: string): string[] {
    const errors: string[] = [];

    Object.entries(userError).forEach(([field, errorValue]) => {
      const message = this.formatFieldError(field, errorValue, statusText);
      if (message) {
        errors.push(message);
      }
    });

    return errors;
  }

  private formatFieldError(field: string, errorValue: unknown, statusText: string): string {
    if (Array.isArray(errorValue)) {
      return `${field}: ${errorValue.join(', ')}`;
    }

    if (typeof errorValue === 'object' && errorValue !== null) {
      return `${field}: ${JSON.stringify(errorValue)}`;
    }

    if (errorValue === true) {
      return `${field}: ${statusText || 'Error'}`;
    }

    const value = String(errorValue).trim();
    return value ? `${field}: ${value}` : '';
  }

  private showFallbackError(error: unknown, status: number, statusText: string): void {
    const errorObj =
      typeof error === 'object' && error !== null
        ? (error as { detail?: unknown; message?: unknown })
        : {};
    const message = String(
      errorObj.detail ?? errorObj.message ?? statusText ?? 'Error desconocido',
    );
    const title = status ? `Error ${status}` : 'Error';
    this.showError(message, title);
  }

  private showError(message: string, title?: string): void {
    this.animation('error', message, title);
  }

  private defaultToastConfig: Partial<IndividualConfig> = {
    timeOut: 3500,
    progressBar: true,
    closeButton: false,
    positionClass: 'toast-top-right',
  };

  animation(
    type: 'success' | 'error' | 'warning' | 'info' | 'show',
    message: string,
    title?: string,
    options?: Partial<IndividualConfig>,
  ) {
    const cfg: Partial<IndividualConfig> = Object.assign(
      {},
      this.defaultToastConfig,
      options || {},
    );

    if (this.toastr) {
      switch (type) {
        case 'success':
          this.toastr.success(message, title || '', cfg as IndividualConfig);
          break;
        case 'error':
          this.toastr.error(message, title || '', cfg as IndividualConfig);
          break;
        case 'warning':
          this.toastr.warning(message, title || '', cfg as IndividualConfig);
          break;
        case 'info':
          this.toastr.info(message, title || '', cfg as IndividualConfig);
          break;
        default:
          this.toastr.show(message, title || '', cfg as IndividualConfig);
      }
      return;
    }
  }

  constructQuery = (obj: object) => {
    const queryObj = obj as Record<string, unknown>;
    const params = new URLSearchParams();
    for (const clave in queryObj) {
      if (
        Object.prototype.hasOwnProperty.call(queryObj, clave) &&
        queryObj[clave] != null &&
        queryObj[clave] !== ''
      ) {
        params.append(clave, String(queryObj[clave]));
      }
    }
    const value = params.toString();
    return value ? '?' + value : '';
  };

  // Función para calcular distancia entre dos puntos en metros usando fórmula haversine
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3; // Radio de la Tierra en metros
    const φ1 = (lat1 * Math.PI) / 180; // φ, λ en radianes
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c; // en metros
    return distance;
  }

  // Crear elemento HTML personalizado para marcadores de mapa
  createMarkerElement(
    type: string,
    store: Store,
    markerElementCache: Map<string, HTMLElement>,
  ): HTMLElement {
    const cacheKey = `${type}-${store.status}-${store.chain?.id || 'no-chain'}-${store.has_sale_this_week ? 'has-sale' : 'no-sale'}-${store.zero_sales_alert ? 'zero-alert' : ''}-${store.greater_than_or_equal_changed ? 'greater-change-alert' : ''}-${store.no_sales_alert ? 'no-alert' : ''}-${store.outdated_alert ? 'outdated-alert' : ''}-${store.discrepancy_alert ? 'discrepancy-alert' : ''}`;

    // Verificar si ya existe en caché
    if (markerElementCache.has(cacheKey)) {
      return markerElementCache.get(cacheKey)!.cloneNode(true) as HTMLElement;
    }

    // Crear contenedor wrapper para marcador y badge
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      position: relative;
      display: inline-block;
    `;

    const markerElement = document.createElement('div');
    markerElement.className = 'custom-operational-marker';

    // Colores según el estado
    const colors = {
      success: '#28a745',
      danger: '#dc3545',
      warning: '#ffc107',
      primary: '#007bff',
      yellow: '#ffeb3b',
      warningStrong: '#ff8c00',
      secondary: '#6c757d',
    };
    let statusColor: string = colors.success;
    statusColor = store.has_sale_this_week ? colors.success : colors.warning;

    if (store.zero_sales_alert) {
      statusColor = colors.success;
    }
    if (store.outdated_alert) {
      statusColor = colors.danger;
    }
    if (store.no_sales_alert) {
      statusColor = colors.danger;
    }
    if (store.discrepancy_alert) {
      statusColor = colors.danger;
    }

    const size = type === 'warehouse' ? '36px' : '32px';
    const hasImage = store.chain?.image;

    if (hasImage) {
      // Marcador con imagen de la cadena
      markerElement.style.cssText = `
        width: ${size};
        height: ${size};
        border-radius: 50%;
        background-color: #ffffff;
        border: 4px solid ${statusColor};
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
        position: relative;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      // Imagen de la cadena
      const img = document.createElement('img');
      img.src = store.chain!.image!;
      img.alt = store.chain?.name || store.name;
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      `;

      // Manejar error de carga de imagen
      img.onerror = () => {
        markerElement.innerHTML = '';
        markerElement.style.backgroundColor = statusColor;
        const fallbackText = document.createElement('div');
        fallbackText.textContent = store.name.charAt(0).toUpperCase();
        fallbackText.style.cssText = `
          color: white;
          font-weight: bold;
          font-size: 14px;
        `;
        markerElement.appendChild(fallbackText);
      };

      markerElement.appendChild(img);
    } else {
      // Marcador sin imagen (fallback al diseño anterior mejorado)
      const borderRadius = type === 'warehouse' ? '20%' : '50%';

      markerElement.style.cssText = `
        width: ${size};
        height: ${size};
        border-radius: ${borderRadius};
        background-color: ${statusColor};
        border: 5px solid #ffffff;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      // Agregar inicial del nombre de la tienda
      const initialText = document.createElement('div');
      initialText.textContent = store.name.charAt(0).toUpperCase();
      initialText.style.cssText = `
        color: white;
        font-weight: bold;
        font-size: 14px;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
      `;
      markerElement.appendChild(initialText);
    }

    const createAlertBadge = (
      tooltipText: string,
      badgeColor: string,
      tooltipColor: string,
      iconClass: string = 'bi bi-exclamation-diamond-fill',
    ): HTMLElement => {
      const alertBadge = document.createElement('div');
      alertBadge.className = 'alert-badge-container';
      alertBadge.innerHTML = `<i class="${iconClass}"></i>`;
      alertBadge.style.cssText = `
        position: absolute;
        top: -13px;
        left: 24px;
        transform: translateX(-50%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
        z-index: 10;
        color: ${badgeColor};
        cursor: pointer;
      `;

      const tooltip = document.createElement('div');
      tooltip.className = 'alert-badge-tooltip';
      tooltip.textContent = tooltipText;
      tooltip.style.cssText = `
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(-8px);
        background-color: ${tooltipColor};
        color: white;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.2s ease, visibility 0.2s ease;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      `;

      const arrow = document.createElement('div');
      arrow.style.cssText = `
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 6px solid ${tooltipColor};
      `;

      tooltip.appendChild(arrow);
      alertBadge.appendChild(tooltip);

      return alertBadge;
    };

    // Agregar marcador al wrapper
    wrapper.appendChild(markerElement);
    const activeAlertsCount = [
      !!store.no_sales_alert,
      !!store.greater_than_or_equal_changed,
      !!store.zero_sales_alert,
    ].filter(Boolean).length;

    /* por encima de todas la mas prioridad */
    if (store.discrepancy_alert) {
      wrapper.appendChild(createAlertBadge('Discrepancia de folio', colors.danger, colors.danger));
    } else if (activeAlertsCount > 1) {
      wrapper.appendChild(
        createAlertBadge(
          'Multiples alertas activas',
          colors.danger,
          colors.danger,
          'bi bi-shield-fill-check',
        ),
      );
    } else if (store.no_sales_alert) {
      wrapper.appendChild(
        createAlertBadge('Alerta de cero ventas en varias semanas', colors.warning, colors.warning),
      );
    } else if (store.greater_than_or_equal_changed) {
      wrapper.appendChild(
        createAlertBadge('Alerta de cambio mayor o igual', colors.primary, colors.primary),
      );
    } else if (store.zero_sales_alert) {
      /* ALERTA DE 0 VENTAS */
      wrapper.appendChild(createAlertBadge('Alerta de cero venta', colors.yellow, colors.yellow));
    }

    markerElementCache.set(cacheKey, wrapper.cloneNode(true) as HTMLElement);

    return wrapper;
  }
}
